import {
  buildConnectorTargetRequest,
  toAppliedTarget,
} from '../lib/connectorTarget';
import type {
  ParameterAdditionOptions,
  ParameterAdditionResult,
} from '../lib/hooks/parameterAddition/models';
import {
  E2E_ACTIVE_DOCUMENT_NAME,
  E2E_ACTIVE_DOCUMENT_PATH,
  resolveActiveProjectKey,
} from '../hooks/useActiveDocumentName';
import {
  countUndoPlanEntries,
  countUndoPlanFamilies,
} from '../lib/parameterUndo/undoPlanCounts';
import { useUIStore } from '../stores/useUIStore';
import type {
  ConnectorParameterUndoEntry,
  ConnectorParameterUndoPlan,
} from '../types/parameterSetupUndo';
import { getE2eBootstrapConfig } from './e2eBootstrap';
import { openE2eSetupRevision } from './e2eSetupRevisionFixture';

/**
 * Screenshot seam for the connector `recovery-required` state. That state only
 * exists after Revit has reversed an applied target and then refused the new
 * one, which the mock Playwright lane can never produce on its own. With
 * `e2eConnectorParameterOutcome=recovery-required` active, a Family-type undo
 * checkpoint is seeded once (never clobbering a real one) and the Revit
 * transport answers "undo succeeded, apply failed". Everything else — the
 * transition decision, the identity checks, the graded failure, the revision
 * event and the recovery guidance — runs as production code.
 */
export const E2E_CONNECTOR_RECOVERY_PLAN_ID = 'e2e-connector-recovery-undo-plan';

export const E2E_CONNECTOR_RECOVERY_APPLY_ERROR =
  'Revit could not create the instance connector parameters in the family editor.';

/**
 * What the seeded checkpoint says is already in the model — one editable family
 * per category a Family-mode apply writes into, with the parameters it received.
 *
 * The transport is stubbed here; the record never is. A checkpoint whose counts
 * are not backed by entries is exactly the damage this flow must refuse, so a
 * fixture shaped that way would photograph the opposite of the state it exists
 * to show — and would be quarantined the moment the page reloaded.
 */
const E2eRecoveryAppliedFamilies: ReadonlyArray<{
  readonly id: string;
  readonly name: string;
  readonly parameterNames: readonly string[];
}> = [
  {
    id: 'e2e-family-elbow-welded',
    name: 'Elbow - Welded - ANSI',
    parameterNames: [
      'PiX_01_Description_ENG',
      'PiX_02_Standard_ENG',
      'PiX_03_Material_ENG',
      'PiX_GN_2_InsulationSpec',
    ],
  },
  {
    id: 'e2e-family-ball-valve-flanged',
    name: 'Ball Valve - Flanged',
    parameterNames: [
      'PiX_01_Description_ENG',
      'PiX_02_Standard_ENG',
      'PiX_03_Material_ENG',
      'PiX_GN_2_InsulationSpec',
    ],
  },
  {
    id: 'e2e-family-pipe-support-shoe',
    name: 'Pipe Support - Shoe',
    parameterNames: [
      'PiX_01_Description_ENG',
      'PiX_02_Standard_ENG',
      'PiX_03_Material_ENG',
    ],
  },
  {
    id: 'e2e-family-centrifugal-pump',
    name: 'Centrifugal Pump',
    parameterNames: [
      'PiX_01_Description_ENG',
      'PiX_02_Standard_ENG',
      'PiX_03_Material_ENG',
    ],
  },
];

/** A fresh copy each time, so no consumer can edit the seed everyone shares. */
function buildE2eRecoveryUndoEntries(): ConnectorParameterUndoEntry[] {
  return E2eRecoveryAppliedFamilies.flatMap((family) =>
    family.parameterNames.map<ConnectorParameterUndoEntry>((parameterName) => ({
      familyId: family.id,
      familyName: family.name,
      parameterName,
      existedBefore: false,
    }))
  );
}

/** The apply can only fail for the parameters the record actually covers. */
const E2eRecoveryParameterCount = countUndoPlanEntries({
  entries: buildE2eRecoveryUndoEntries(),
});

/** The two Revit calls the connector target transaction is allowed to make. */
export interface ConnectorTargetTransport {
  addParameters: (options?: ParameterAdditionOptions) => Promise<ParameterAdditionResult>;
  undoLastConnectorParameterAddition: () => Promise<boolean>;
}

function isConnectorRecoveryFixtureActive(search: string): boolean {
  return getE2eBootstrapConfig(search)?.connectorParameterOutcome === 'recovery-required';
}

/** The Family/type target the model is pretending to already hold. */
export function buildE2eConnectorRecoveryCheckpoint(): ConnectorParameterUndoPlan | null {
  const projectKey = resolveActiveProjectKey({
    documentName: E2E_ACTIVE_DOCUMENT_NAME,
    documentPath: E2E_ACTIVE_DOCUMENT_PATH,
  });
  if (!projectKey) {
    return null;
  }

  const applied = buildConnectorTargetRequest({
    projectKey,
    documentName: E2E_ACTIVE_DOCUMENT_NAME,
    documentPath: E2E_ACTIVE_DOCUMENT_PATH,
    target: { destination: 'family', bindingKind: 'type' },
    setupRevisionId: useUIStore.getState().setupRevisionId,
    setupFields: null,
  });
  if (!applied) {
    return null;
  }

  const entries = buildE2eRecoveryUndoEntries();

  return {
    id: E2E_CONNECTOR_RECOVERY_PLAN_ID,
    createdAt: new Date().toISOString(),
    projectKey,
    projectParameterNames: [],
    familyCount: countUndoPlanFamilies({ entries }),
    entryCount: countUndoPlanEntries({ entries }),
    status: 'available',
    entries,
    appliedTarget: toAppliedTarget(applied),
  };
}

export function ensureE2eConnectorRecoveryCheckpointSeeded(
  search: string = window.location.search
): void {
  if (!isConnectorRecoveryFixtureActive(search)) {
    return;
  }
  const { connectorParameterUndoPlan, setConnectorParameterUndoPlan } = useUIStore.getState();
  if (connectorParameterUndoPlan) {
    return;
  }

  const checkpoint = buildE2eConnectorRecoveryCheckpoint();
  if (checkpoint) {
    setConnectorParameterUndoPlan(checkpoint);
  }
}

/**
 * Reopens setup for the fixture document so the Connectors step can actually
 * reach `recoveryRequired`: that status is only legal out of `reconciling`,
 * which a revision must already be open to enter, and a fresh browser load
 * sits at `ready`.
 */
export function ensureE2eSetupRevisionOpened(
  search: string = window.location.search
): void {
  if (!isConnectorRecoveryFixtureActive(search)) {
    return;
  }

  // The boundary is named on purpose: the e2e history for this screen is
  // ['welcome', 'connectorBinding'], so the default would leave the step.
  openE2eSetupRevision('connectorBinding');
}

/** Reverses only the seeded checkpoint, so a real plan is never rewritten. */
async function undoSeededCheckpoint(): Promise<boolean> {
  const { connectorParameterUndoPlan, setConnectorParameterUndoPlan } = useUIStore.getState();
  if (connectorParameterUndoPlan?.id === E2E_CONNECTOR_RECOVERY_PLAN_ID) {
    setConnectorParameterUndoPlan({ ...connectorParameterUndoPlan, status: 'undone' });
  }
  return true;
}

async function failApply(): Promise<ParameterAdditionResult> {
  return {
    success: false,
    addedCount: 0,
    failedCount: E2eRecoveryParameterCount,
    requestedCount: E2eRecoveryParameterCount,
    failureMessages: [E2E_CONNECTOR_RECOVERY_APPLY_ERROR],
    error: E2E_CONNECTOR_RECOVERY_APPLY_ERROR,
  };
}

export function resolveE2eConnectorTargetTransport(
  real: ConnectorTargetTransport,
  search: string = window.location.search
): ConnectorTargetTransport {
  if (!isConnectorRecoveryFixtureActive(search)) {
    return real;
  }

  return {
    addParameters: failApply,
    undoLastConnectorParameterAddition: undoSeededCheckpoint,
  };
}
